import React, { useEffect, useState } from "react";
import {
  Phone, UserCheck, Info, X, PhoneCall,
  Building2, Briefcase, Loader2
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import NotificationAlert from "../../../../CustomAlerts/NotificationAlert";
import { SaveBasicRecruitmentDetails, StartCall } from "../../Leads/Services/RecruitmentService";

const InitiateCallDrawer = ({ open, onClose, lead }) => {
  const navigate = useNavigate();

  const [alert, setAlert] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [designation, setDesignation] = useState("");
  const [callStarted, setCallStarted] = useState(false)
  const [loadingType, setLoadingType] = useState(null);

  useEffect(() => {
    if (open && lead) {
      setOrgName("");
      setDesignation("");
      setCallStarted(false);
    }
  }, [open, lead]);

  const handleClose = () => {
    setOrgName("");
    setDesignation("");
    setLoadingType(null);
    setCallStarted(false);
    onClose();
  };

  const handleSaveCall = async (status) => {
    if (!orgName.trim()) {
      setAlert({
        type: "warning",
        message: "Please enter organization name.",
      });
      return;
    }

    if (!designation.trim()) {
      setAlert({
        type: "warning",
        message: "Please enter designation.",
      });
      return;
    }

    if(status === "Connected"){
      handleClose();
      navigate(`/Edit-Lead/${lead?.leadUID}`, {
        state: {
          targetOrganization: orgName,
          targetRole: designation
        }
      });
    }
    else{
      try {
      setLoadingType(status);

      const payload = {
        leadUID: lead?.leadUID,
        organization: orgName,
        designation: designation,
        callResponse: status,
      };

      const res = await SaveBasicRecruitmentDetails(payload);

      if (res?.status === "success") {
        setAlert({
          type: "success",
          message: res?.message || "Call details saved successfully",
        });

        setTimeout(() => {
          handleClose();
        }, 1200);
      } else {
        setAlert({
          type: "error",
          message: res?.message || "Failed to save details.",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error?.message || "Something went wrong",
      });
    } finally {
      setLoadingType(null);
    }
    }    
  };

 const handleStartCall = async () => {
  try {
    const payload = {
      mobileNumber: lead?.phoneNo,  
      leadUID: lead?.leadUID,
    };

    const res = await StartCall(payload);

    if (res?.status === "success") {
      setAlert({
        type: "success",
        message: "Call request sent",
      });
      setCallStarted(true);
    }
  } catch (error) {
    setAlert({
      type: "error",
      message: error?.message || "Failed to start call",
    });
  }
};

  return (
    <>
      {/* MAIN WRAPPER */}
      <div
        className={`fixed inset-0 z-50 ${
          open ? "visible" : "invisible"
        } transition-all duration-500`}
      >
        {/* OVERLAY */}
        <div
          className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-500
            ${open ? "opacity-100" : "opacity-0"}`}
          onClick={handleClose}
        />

        {/* DRAWER */}
        <div
          className={`fixed top-0 right-0 w-full max-w-xl bg-white h-full shadow-2xl flex flex-col transform transition-transform duration-500 ease-in-out
            ${open ? "translate-x-0" : "translate-x-full"}`}
        >
          {/* HEADER */}
          <div className="flex items-center justify-between p-5 border-b">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <PhoneCall className="text-blue-700" size={18} />
              </div>

              <div>
                <h2 className="font-semibold text-lg text-slate-800">
                  Initiate Call
                </h2>

                <p className="text-xs text-slate-500 mt-0.5">
                  Update call response and recruitment details
                </p>
              </div>
            </div>

            <button
              onClick={handleClose}
              className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center transition"
            >
              <X className="text-slate-500" size={18} />
            </button>
          </div>

          {/* BODY */}
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* LEAD INFO */}
            {lead && (
              <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-lg">
                    {lead?.name?.charAt(0)}
                  </div>

                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 text-sm">
                      {lead?.name}
                    </p>

                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                      <Phone size={12} />

                      <span>{lead?.phoneNo}</span>
                    </div>

                    <p className="text-xs text-slate-500 mt-1">
                      Assigned to{" "}
                      <span className="font-medium">
                        {lead?.ownerName || "-"}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* FORM */}
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50/40 p-5 space-y-5 shadow-sm">
              {/* HEADER */}
              <div className="flex items-center gap-2 text-blue-700 font-semibold">
                <UserCheck size={18} />
                Call Information
              </div>

              {/* ORGANIZATION */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Contacted For Organization{" "}
                  <span className="text-red-500">*</span>
                </label>

                <div className="relative">
                  <Building2
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Enter organization name"
                    className="w-full h-11 rounded-xl border border-slate-200 bg-white/80 pl-10 pr-4 text-sm outline-none hover:border-slate-300 focus:ring-1 focus:ring-blue-400 focus:border-blue-500 transition"
                  />
                </div>
              </div>

              {/* DESIGNATION */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Contacted For Designation/Role{" "}
                  <span className="text-red-500">*</span>
                </label>

                <div className="relative">
                  <Briefcase
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                    placeholder="Enter designation / role"
                    className="w-full h-11 rounded-xl border border-slate-200 bg-white/80 pl-10 pr-4 text-sm outline-none hover:border-slate-300 focus:ring-1 focus:ring-blue-400 focus:border-blue-500 transition"
                  />
                </div>
              </div>

              <button
                onClick={handleStartCall}
                className="w-full py-3 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition"
              >
                Connect Candidate
              </button>
            </div>

            {/* INFO SECTION */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              {/* HEADER */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-blue-100">
                  <Info size={16} className="text-blue-600" />
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Call Outcome Guidelines
                  </h3>

                  <p className="text-xs text-slate-500 mt-1">
                    Update the candidate response correctly after the call
                    attempt.
                  </p>
                </div>
              </div>

              {/* DIVIDER */}
              <div className="h-px bg-slate-200 my-4" />

              {/* GUIDELINES */}
              <div className="space-y-4 text-sm">
                {/* CONNECTED */}
                <div className="flex items-start gap-3">
                  <PhoneCall size={16} className="text-green-600 mt-0.5" />

                  <div>
                    <p className="font-medium text-slate-800">Connected</p>

                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Candidate answered the call and discussion was successful.
                      You’ll be redirected to edit lead details.
                    </p>
                  </div>
                </div>

                {/* NOT CONNECTED */}
                <div className="flex items-start gap-3">
                  <Phone size={16} className="text-orange-500 mt-0.5" />

                  <div>
                    <p className="font-medium text-slate-800">Not Connected</p>

                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Candidate did not answer or communication was
                      unsuccessful. The Call details will still be saved for
                      tracking purposes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {callStarted && (
            <>
              {/* FOOTER */}
              <div className="p-5 border-t border-slate-200 bg-white/95 backdrop-blur flex gap-3">
                {/* NOT CONNECTED */}
                <button
                  onClick={() => handleSaveCall("Not Connected")}
                  disabled={loadingType !== null}
                  className="w-1/2 h-11 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-100 hover:border-slate-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loadingType === "Not Connected" && (
                    <Loader2 size={16} className="animate-spin" />
                  )}
                  Not Connected
                </button>

                {/* CONNECTED */}
                <button
                  onClick={() => handleSaveCall("Connected")}
                  disabled={loadingType !== null}
                  className="w-1/2 h-11 rounded-xl bg-blue-700 text-white font-semibold hover:bg-blue-800 hover:shadow-lg hover:shadow-blue-100 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loadingType === "Connected" && (
                    <Loader2 size={16} className="animate-spin" />
                  )}
                  Connected
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ALERT */}
      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert?.type}
          message={alert?.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default InitiateCallDrawer;